import { getApiUrl, log, isDevelopment, env } from '../config/env';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  statusCode?: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Unified API service for admin portal
 * Handles all API calls with consistent error handling and 401 redirect
 */
export class AdminApiService {
  private static instance: AdminApiService;
  private token: string | null = null;
  private onUnauthorized: (() => void) | null = null;

  private constructor() {
    // Initialize token from localStorage
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      this.token = storedToken;
      log.debug('AdminApiService initialized with token from localStorage');
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AdminApiService {
    if (!AdminApiService.instance) {
      AdminApiService.instance = new AdminApiService();
    }
    return AdminApiService.instance;
  }

  /**
   * Set callback for unauthorized (401) responses
   */
  setOnUnauthorized(callback: () => void): void {
    this.onUnauthorized = callback;
  }

  /**
   * Update token (call after login)
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
  }

  /**
   * Clear token (call on logout)
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem('token');
  }

  /**
   * Unified API request method
   * Handles data, messages, errors, and 401 redirects
   */
  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = getApiUrl(endpoint);

      const headers: HeadersInit = {
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      };

      // Add authorization header if token exists
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      // Add language header based on current language
      const currentLanguage = localStorage.getItem('language') || 'en';
      headers['Accept-Language'] = currentLanguage;

      log.debug('API Request:', { url, method: options.method || 'GET' });

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Try to parse response as JSON
      const contentType = response.headers.get('content-type');
      let responseData: any = null;

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
        } catch (parseError) {
          log.error('Failed to parse JSON response:', parseError);
          return {
            success: false,
            error: 'Invalid response format',
            message: 'Server returned invalid JSON',
            statusCode: response.status,
          };
        }
      } else {
        const text = await response.text();
        if (text) {
          responseData = { message: text };
        }
      }

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        log.warn('Received 401 Unauthorized, redirecting to login');
        this.clearToken();
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }
        return {
          success: false,
          error: 'Unauthorized',
          message: 'Your session has expired. Please login again.',
          statusCode: 401,
        };
      }

      // Check if response is ok
      if (!response.ok) {
        log.error('API Error Response:', { status: response.status, data: responseData });
        return {
          success: false,
          error: responseData?.error || responseData?.message || `HTTP ${response.status}`,
          message: responseData?.message || `Request failed with status ${response.status}`,
          statusCode: response.status,
        };
      }

      // Success response
      log.debug('API Success Response:', responseData);
      return {
        success: true,
        data: responseData?.data || responseData,
        message: responseData?.message,
        statusCode: response.status,
      };
    } catch (error) {
      log.error('API Request Error:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'An unexpected error occurred',
        statusCode: 0,
      };
    }
  }

  async requestFile(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
    try {
      const url = getApiUrl(endpoint);
      const headers: HeadersInit = {
        ...options.headers,
      };

      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }
      headers['Accept-Language'] = localStorage.getItem('language') || 'en';

      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        this.clearToken();
        this.onUnauthorized?.();
        return {
          success: false,
          error: 'Unauthorized',
          message: 'Your session has expired. Please login again.',
          statusCode: 401,
        };
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let errorMessage = `Request failed with status ${response.status}`;
        if (contentType.includes('application/json')) {
          const errorResponse = await response.json();
          errorMessage = errorResponse?.message || errorResponse?.error || errorMessage;
        } else {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        return {
          success: false,
          error: errorMessage,
          message: errorMessage,
          statusCode: response.status,
        };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
      const fileName = fileNameMatch
        ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, '').trim())
        : undefined;

      return {
        success: true,
        data: { blob, fileName },
        statusCode: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'An unexpected error occurred',
        statusCode: 0,
      };
    }
  }

  /**
   * Generic method for any API call
   * Useful for endpoints not covered by specific methods
   */
  async call<T = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    data?: any
  ): Promise<ApiResponse<T>> {
    const options: RequestInit = { method };
    if (data) {
      options.body = JSON.stringify(data);
    }
    return this.request<T>(endpoint, options);
  }

  async upload<T = any>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    headers['Accept-Language'] = localStorage.getItem('language') || 'en';

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers,
    });
  }
}

// Export singleton instance
export const adminApi = AdminApiService.getInstance();

/**
 * Enhanced fetch wrapper with proper error handling for JSON responses
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const url = getApiUrl(endpoint);
    log.debug('API Request:', { url, options });

    const currentLanguage = localStorage.getItem('language') || 'en';

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': currentLanguage,
        ...options.headers,
      },
      ...options,
    });

    log.debug('API Response Status:', response.status);

    // Check if response is ok
    if (!response.ok) {
      // Try to parse error response as JSON
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } else {
          // If not JSON, it might be HTML (404 page, etc.)
          const textResponse = await response.text();
          if (textResponse.includes('<!DOCTYPE')) {
            errorMessage = `API endpoint not found: ${endpoint}`;
          } else {
            errorMessage = textResponse || errorMessage;
          }
        }
      } catch (parseError) {
        log.warn('Failed to parse error response:', parseError);
      }

      throw new ApiError(errorMessage, response.status, response);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const textResponse = await response.text();
      if (textResponse.includes('<!DOCTYPE')) {
        throw new ApiError('API endpoint returned HTML instead of JSON. Please check the API URL configuration.');
      }
      throw new ApiError('API response is not JSON');
    }

    // Parse JSON response
    const data = await response.json();
    log.debug('API Response Data:', data);

    return {
      success: true,
      data,
      message: data.message,
    };

  } catch (error) {
    log.error('API Request Error:', error);

    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = isDevelopment()
        ? `Network error: Cannot connect to API server at ${env.apiBaseUrl}. Please ensure the backend server is running.`
        : 'Network error. Please check your internet connection and try again.';

      return {
        success: false,
        error: networkError,
      };
    }

    // Handle JSON parsing errors
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      return {
        success: false,
        error: 'Invalid response format from server. Please contact support.',
      };
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

/**
 * Authentication API methods
 */
export const authApi = {
  async login(credentials: { phoneOrEmail: string; password: string }) {
    return apiRequest('/auth/signin-admin', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  async signup(userData: { name: string; email: string; phone: string; password: string }) {
    return apiRequest('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  async confirmOtp(otpData: { email: string; phone: string; code: string }) {
    return apiRequest('/auth/confirmotp', {
      method: 'POST',
      body: JSON.stringify(otpData),
    });
  },

  async forgotPassword(email: string) {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(resetData: { token: string; password: string }) {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(resetData),
    });
  },

  async checkAuth(token: string) {
    return apiRequest('/auth/verify', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  async checkAuthorized(token: string) {
    try {
      const url = getApiUrl('/auth/authorized');
      log.debug('Making authorization check request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      log.debug('Authorization check response status:', response.status);

      // For authorization check, we only care about the status code
      // 200 = authorized, anything else = not authorized
      if (response.ok) {
        log.debug('Authorization check successful');
        return {
          success: true,
          data: null,
          message: 'Authorized',
        };
      } else {
        log.warn('Authorization check failed with status:', response.status);
        return {
          success: false,
          error: `Authorization failed (${response.status})`,
          data: null,
        };
      }
    } catch (error) {
      log.error('Authorization check network error:', error);
      return {
        success: false,
        error: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        data: null,
      };
    }
  },
};
